import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Badge,
  Tooltip,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  VStack,
  Button,
  Text,
} from "@chakra-ui/react";
import { FiEdit } from "react-icons/fi";
import { DarwinTool, DarwinToolListWrapper, DarwinToolType } from "build-ts/darwin_agent";
import { getToolsList, updateTool } from "../../services/NewDarwinEvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { epochConverterWithTimeZone } from "../../utils/dateUtils";
import { useUser } from "context/UserContext";
import { getToolTypeColorForDarwinEva } from "utils/helperFuncUtils";


const ToolTable: React.FC = () => {
  const hasMounted = useRef(false);
  const { defaultTimeZone } = useUser();

  // State for data
  const [tools, setTools] = useState<DarwinTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Tool filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
  });
  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Edit modal state
  const { isOpen: isEditToolOpen, onOpen: onEditToolOpen, onClose: onEditToolClose } = useDisclosure();
  const [editingTool, setEditingTool] = useState<DarwinTool | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch tools from Darwin API
  const fetchTools = useCallback(async (
      reset = false,
      currentSortBy = "created_date",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("name", currentFilters.search);
      }
      if (currentFilters.type && currentFilters.type !== "all") {
        params.append("tool_type", currentFilters.type);
      }

      const result: DarwinToolListWrapper = DarwinToolListWrapper.fromJSON(await getToolsList(params));

      if (result.tools) {
        // Always replace the tools list, never append
        setTools(result.tools);

        if (result.total) {
          setPagination({
            total: result.total,
            has_more: (currentOffset + limit) < result.total,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no tools returned
        setTools([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchTools(true);
      };
      loadData();
    }
  }, [fetchTools]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchTools(false, sortBy, sortOrder, filters, offset);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchTools(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchTools, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchTools(true, sortBy, sortOrder, filters, 0);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all" };
    setFilters(clearedFilters);
    setSortBy("created_date");
    setSortOrder("asc");
    await fetchTools(true, "created_date", "asc", clearedFilters, 0);
  }, [fetchTools]);

  const handleEditTool = (tool: DarwinTool) => {
    setEditingTool(tool);
    setEditFormData({
      name: tool.header?.name || "",
      description: tool.header?.description || "",
    });
    onEditToolOpen();
  };

  const handleUpdateTool = async () => {
    if (!editingTool) return;

    setIsUpdating(true);
    const response = await updateTool(editingTool.header?.id, {
      header: {
        name: editFormData.name,
        description: editFormData.description,
      },
    });

    // Only update the tool in the list if the update was successful
    if (response) {
      setTools(prevTools =>
        prevTools.map(tool =>
          tool.header?.id === editingTool.header?.id
            ? {
                ...tool,
                header: {
                  ...tool.header,
                  name: response.header?.name,
                  description: response.header?.description,
                  last_modified_date: response.header?.last_modified_date,
                }
              }
            : tool
        )
      );
      onEditToolClose();
      setEditingTool(null);
    }

    setIsUpdating(false);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Get tool types from DarwinToolType enum
  const toolTypes = useMemo(() => {
    return Object.values(DarwinToolType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_TOOL_TYPE"
    ) as string[];
  }, []);

  // Table configuration
  const tableColumns: TableColumn<DarwinTool>[] = [
    {
      key: "name",
      label: "Tool Name",
      sortable: true,
      className: "eva-name-column",
      render: (tool) => (
        <Tooltip label={tool.header?.name} placement="top">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} wordBreak="break-word" mb={0}>
            {tool.header?.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "eva-type-column",
      render: (tool) => {
        const toolType = tool.header?.type as keyof typeof DarwinToolType;
        return (
          <Badge colorScheme={getToolTypeColorForDarwinEva(DarwinToolType[toolType])}>
            {tool.header?.type || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "description",
      label: "Description",
      className: "eva-description-column",
      render: (tool) => (
        <Tooltip label={tool.header?.description} placement="top">
          <Text fontSize="sm" color="gray.600" noOfLines={2} wordBreak="break-word" mb={0}>
            {tool.header?.description}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "is_response_tool",
      label: "Response Tool",
      className: "eva-is-response-tool-column",
      render: (tool) => (
        <Badge colorScheme={tool.header?.is_response_tool ? "green" : "red"} size="sm">
          {tool.header?.is_response_tool ? "True" : "False"}
        </Badge>
      ),
    },
    {
      key: "created_date",
      label: "Created",
      sortable: true,
      className: "eva-date-column",
      render: (tool) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(tool.header?.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
    {
      key: "last_modified_date",
      label: "Modified",
      sortable: true,
      className: "eva-date-column",
      render: (tool) => (
        <Text fontSize="sm" color="gray.600" mb={0}>
          {epochConverterWithTimeZone(tool.header?.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} {defaultTimeZone.toUpperCase()}
        </Text>
      ),
    },
  ];

  const tableFilters: TableFilter[] = [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search tools...",
      width: "300px",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      options: [
        { value: "all", label: "All Types" },
        ...toolTypes.map((type: string) => ({ value: type, label: type })),
      ],
      width: "200px",
    },
  ];

  const rowActions = (tool: DarwinTool) => (
    <Tooltip label="Edit Tool">
      <IconButton
        icon={<FiEdit />}
        size="sm"
        variant="ghost"
        onClick={() => handleEditTool(tool)}
        aria-label="Edit tool"
      />
    </Tooltip>
  );

  return (
    <>
      <Table
        data={tools}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        rowActions={rowActions}
        getItemId={(tool) => tool.header?.id || ""}
        emptyMessage="No tools found"
        emptySubMessage="Try adjusting your search criteria"
      />

      {/* Tool Edit Modal */}
      <Modal isOpen={isEditToolOpen} onClose={onEditToolClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent className="eva-modal-content">
          <ModalHeader>Edit Tool</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar">
            {editingTool && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Tool Type</FormLabel>
                  <Input
                    value={editingTool.header?.type || ""}
                    isReadOnly
                    bg="gray.50"
                    cursor="not-allowed"
                    color="gray.600"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Tool Name</FormLabel>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => handleEditFormChange("name", e.target.value)}
                    placeholder="Enter tool name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Args Schema JSON</FormLabel>
                  <Textarea
                    value={editingTool.args_schema || ""}
                    isReadOnly
                    rows={5}
                    bg="gray.50"
                    color="gray.600"
                    cursor="not-allowed"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Is Response Tool</FormLabel>
                  <Input
                    value={editingTool.header?.is_response_tool ? "True" : "False"}
                    isReadOnly
                    bg="gray.50"
                    cursor="not-allowed"
                    color="gray.600"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => handleEditFormChange("description", e.target.value)}
                    placeholder="Enter tool description"
                    rows={6}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onEditToolClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateTool}
              isLoading={isUpdating}
              loadingText="Updating..."
            >
              Update Tool
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ToolTable;
